/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */
import Backbone from 'backbone';

import PageableCollection from '@/backbone/PageableCollection';
import ButtonCell from '@/backgrid/ButtonCell';
import StringCell from '@/backgrid/StringCell';
import { DateCell } from '@/backgrid/MomentCell';
import GridView from '@/views/GridView';
import MultiButtonCell from '@/backgrid/MultiButtonCell';
import showModal from '@/util/showModal';
import ConfirmationModalView from '@/views/ConfirmationModalView';
import showErrorMessage from '@/util/showErrorMessage';
import showSuccessMessage from '@/util/ShowSuccessMessage';

import RemoveUserCommandAccessByExternalUserIDAndCommandID from '../../../command/services/RemoveUserCommandAccessByExternalUserIDAndCommandID';

import './CustomerCommandsGridView.css';

const CustomerCommandsGridView = GridView.extend({
  initialize() {
    const self = this;
    const isSelfService = this.model.get("isSelfService");

    this.options.columns = [
      {
        name: 'actingAsRadio',
        label: 'Acting As',
        cell: ButtonCell.extend({
          name: '',
          buttonClass: 'btn-sm radio-check-symbol text-white', 
          labelClass() {
            return this.model.get('id') === self.model.get('actingAsCommandId')
              ? 'fa fa-check-circle fa-lg fa-fw text-primary'
              : 'fa fa-circle-o fa-lg fa-fw text-primary';
          },
          ariaLabel() {
            return this.model.get('id') === self.model.get('actingAsCommandId')
              ? 'checked command customer is acting on behalf of'
              : 'not checked'
          },
          onClick: this.onClickSetActingAsCommand.bind(this)
        }),
        headerCell: 'custom',
        width: 11,
        renderable: !!isSelfService
      },
      {
        name: 'dodac',
        label: 'DODAAC',
        cell: StringCell,
        headerCell: 'custom',
        width: isSelfService ? 12 : 10,
        editable: false
      },
      {
        name: 'name',
        label: 'Command Name',
        cell: StringCell,
        headerCell: 'custom',
        width: isSelfService ? 40 : 30,
        editable: false,
        sortable: true,
      },
      {
        name: 'serviceBranchName',
        label: 'Service Branch',
        cell: StringCell,
        headerCell: 'custom',
        width: 10,
        editable: false,
        renderable: !isSelfService
      },
      {
        name: 'regionName',
        label: 'Region',
        cell: StringCell,
        headerCell: 'custom',
        width: isSelfService ? 17 : 10,
        editable: false
      },
      {
        name: 'requestedOn',
        label: 'Requested On',
        cell: DateCell,
        headerCell: 'custom',
        width: 15,
        editable: false,
        renderable: !isSelfService
      },
      {
        name: 'decidedOn',
        label: 'Decided On',
        cell: DateCell,
        headerCell: 'custom',
        width: 15,
        editable: false,
        renderable: !isSelfService
      },
      {
        name: 'status',
        label: 'Status',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        editable: false,
        renderable: !isSelfService
      },
      {
        name: '',
        label: '',
        sortable: false,
        cell: MultiButtonCell.extend({
          buttons: [
            {
              name: 'View',
              buttonClass: 'btn-primary btn-sm',
              href(model) {
                return `#command/${model.get('id')}`;
              },
              newTab: true
            },
            {
              name: 'Remove',
              buttonClass: 'btn-danger btn-sm ladda-button',
              onClick: this.onClickRemoveCustomer.bind(this),
              disabled(model) {
                if (model.get('status') !== 'Approved') {
                  return true;
                } else return false;
              }
            }
          ]
        }),
        headerCell: 'custom',
        width: 20,
        editable: false
      }
    ];

    const commands = this.model.get('commands');
    this.options.collection = new PageableCollection(commands, { pageSize: 25 });
    this.options.emptyText = 'No Commands Found';
  },

  onClickSetActingAsCommand(e, model) {
    e?.preventDefault();
    this.model.set('actingAsCommandId', model.get('id'));
    this.triggerMethod('actingAsCommand:updated')
  },

  onClickRemoveCustomer(e, model) {
    e.preventDefault();
    const message = `Are you sure you want to revoke command access for ${model.get('name')}?`;
    if (model.get('status') !== 'Approved') {
      this.showNoAccessError(model.get('name'));
      return;
    }

    showModal({
      title: 'Confirm Revoking Command Access',
      view: new ConfirmationModalView({
        confirmText: 'Remove',
        model: new Backbone.Model({
          message,
          onConfirm: async () => {
            try {
              const userID = this.model.get('userId');
              const commandID = model.get('id');
              await RemoveUserCommandAccessByExternalUserIDAndCommandID(userID, commandID);
            } catch (err) {
              this.showRemoveError();
              return err;
            }
          },
          onConfirmDone: async () => {
            if(this.model.get('isSelfService')) {
              this.options.collection.fullCollection.remove(model);
            } else {
              model.set('status', 'Removed');
              this.render();
            }
            this.showRemoveSuccess();
          }
        })
      })
    });
  },

  showRemoveSuccess() {
    showSuccessMessage({
      code: 'deleteSuccess',
      title: 'Command Access Revoked',
      message: 'Command Access successfully revoked.',
      hideAfter: 5
    });
  },

  showRemoveError() {
    showErrorMessage({
      errorCode: 'deleteError',
      errorTitle: 'Command Access Revoked Error',
      errorMessage: 'An error occurred while removing access to command for this user. Try again or contact an administrator if your issue persists.'
    });
  },

  showNoAccessError(name) {
    showErrorMessage({
      errorCode: 'deleteError',
      errorTitle: 'Access Error ',
      errorMessage: `User already does not have access to command ${name}.`
    });
  }
});

export default CustomerCommandsGridView;
